import React, { useState } from "react"
import { Box, Button, Container, Divider, Grid, Link, Stack, Typography } from "@mui/material"
import SportsBasketballIcon from "@mui/icons-material/SportsBasketball"
import MapIcon from "@mui/icons-material/Map"
import GroupsIcon from "@mui/icons-material/Groups"
import ForumIcon from "@mui/icons-material/Forum"
import EventAvailableIcon from "@mui/icons-material/EventAvailable"
import { tokens } from "./theme/tokens"
import Screenshot from "./screenshots"

const features = [
  {
    title: "Chill with like-minded bros",
    copy: "Tap into crews that get your pace and your hobbies.",
    icon: <GroupsIcon fontSize="medium" />
  },
  {
    title: "Find events near you",
    copy: "Pickup games, fishing trips, hikes, and hangouts.",
    icon: <MapIcon fontSize="medium" />
  },
  {
    title: "Create your own hangouts",
    copy: "Post it, set the time, and rally the guys.",
    icon: <EventAvailableIcon fontSize="medium" />
  },
  {
    title: "Chat with attendees",
    copy: "Lock in details before you show up.",
    icon: <ForumIcon fontSize="medium" />
  },
  {
    title: "Show up & connect",
    copy: "Real-life events build real friendships.",
    icon: <SportsBasketballIcon fontSize="medium" />
  }
]

const steps = [
  {
    step: "01",
    title: "Browse events",
    copy: "Find what fits your schedule and your interests."
  },
  {
    step: "02",
    title: "Create your own",
    copy: "Host a workout, game night, or weekend trip."
  },
  {
    step: "03",
    title: "Join & coordinate",
    copy: "Lock in the crew and pull up."
  }
]

const categories = ["Sports", "Fitness", "Outdoors", "Games", "Social", "Chill"]

// const testimonials = [
//   {
//     quote: "Moved to a new city and BroStrike got me into a weekly run club. Solid crew.",
//     name: "Marcus D., Austin"
//   },
//   {
//     quote: "Pickup hoops turned into real friends. We hang every weekend now.",
//     name: "Trevor P., Denver"
//   },
//   {
//     quote: "Fishing trips, poker nights, gym sessions. It finally feels like home.",
//     name: "Andre L., Tampa"
//   }
// ]

const faq = [
  {
    question: "Is BroStrike a dating app?",
    answer: "No. BroStrike is all about real-life events and building brotherhood."
  },
  {
    question: "What kinds of events are on BroStrike?",
    answer: "Sports, fitness, outdoor trips, game nights, and low-key hangouts."
  },
  {
    question: "Can I host my own event?",
    answer: "Absolutely. Create a hangout and invite the crew."
  }
]

const Home = () => {
  const [showCookieBanner, setShowCookieBanner] = useState(true)

  return (
    <Box sx={{ bgcolor: "background.default", color: "text.primary", width: "100%", minHeight: "100vh" }}>
      <Box component="main" id="top">
        <Box
          component="section"
          sx={{
            py: { xs: 6, md: 10 },
            position: "relative",
            overflow: "hidden"
          }}>
          <Container>
            <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <Typography variant="h1" component="h1">
                    Every dude needs a group of guys. Find yours.
                  </Typography>
                  <Typography variant="h6" sx={{ color: "text.secondary" }}>
                    BroStrike is an events-based app for men to connect in person over shared interests.
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      href="https://play.google.com/store/apps/details?id=com.brostrike">
                      On GooglePlay Store
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      href="https://apps.apple.com/us/app/brostrike/id6757090485">
                      On AppStore
                    </Button>
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ pt: 2 }}>
                    {["Real-life meetups", "Shared interests", "Built for brotherhood"].map(item => (
                      <Box
                        key={item}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          border: `1px solid ${tokens.colors.border}`,
                          padding: "0.6rem 0.9rem",
                          borderRadius: tokens.radius.tight,
                          backgroundColor: tokens.colors.background.surface,
                          fontSize: "0.9rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em"
                        }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "2px",
                            backgroundColor: tokens.colors.accent.primary
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Screenshot />
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box component="section" sx={{ py: { xs: 6, md: 9 } }}>
          <Container>
            <Stack spacing={3}>
              <Typography variant="h2">What is BroStrike?</Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 720 }}>
                BroStrike is a brotherhood-first community for men who want real-life connections. Find events that
                match your interests, show up, and build a crew without the awkward small talk.
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 720 }}>
                Whether it&apos;s a pickup game, a trail hike, or a simple chill night, BroStrike makes it easy to meet
                up and keep it real.
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  border: `2px solid ${tokens.colors.accent.primary}`,
                  padding: "0.5rem 1rem",
                  borderRadius: tokens.radius.base,
                  backgroundColor: "rgba(244, 176, 0, 0.08)",
                  textTransform: "uppercase",
                  letterSpacing: "0.18em",
                  width: "fit-content"
                }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: tokens.colors.accent.primary
                  }}
                />
                <Typography variant="subtitle2">No awkward intros</Typography>
              </Box>
            </Stack>
          </Container>
        </Box>

        <Box
          component="section"
          id="why-brostrike"
          sx={{ py: { xs: 6, md: 10 }, backgroundColor: tokens.colors.background.surface }}>
          <Container>
            <Stack spacing={4}>
              <Box>
                <Typography variant="h2">Why BroStrike?</Typography>
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  Built for men who want real friends, real plans, and zero fluff.
                </Typography>
              </Box>
              <Grid container gap={2} display="flex" justifyContent="center">
                {features.map(feature => (
                  <Grid item xs={12} sm={6} md={4} key={feature.title}>
                    <Box
                      sx={{
                        border: `2px solid ${tokens.colors.border}`,
                        borderRadius: tokens.radius.card,
                        padding: "1.5rem",
                        height: "100%",
                        backgroundColor: tokens.colors.background.default,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2
                      }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: `2px solid ${tokens.colors.accent.primary}`,
                          borderRadius: tokens.radius.base,
                          color: tokens.colors.accent.primary
                        }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" sx={{ textTransform: "uppercase" }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {feature.copy}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </Box>

        <Box component="section" id="how-it-works" sx={{ py: { xs: 6, md: 10 } }}>
          <Container>
            <Stack gap={4}>
              <Typography variant="h2">How it works</Typography>
              <Grid container spacing={3}>
                {steps.map(step => (
                  <Grid item xs={12} md={4} key={step.step}>
                    <Box
                      sx={{
                        border: `2px solid ${tokens.colors.border}`,
                        borderRadius: tokens.radius.card,
                        padding: "1.5rem",
                        backgroundColor: tokens.colors.background.surface,
                        height: "100%"
                      }}>
                      <Typography variant="h3" sx={{ color: tokens.colors.accent.primary, mb: 1 }}>
                        {step.step}
                      </Typography>
                      <Typography variant="h6" sx={{ textTransform: "uppercase" }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                        {step.copy}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </Box>

        <Box
          component="section"
          id="events"
          sx={{ py: { xs: 6, md: 9 }, backgroundColor: tokens.colors.background.surface }}>
          <Container>
            <Stack spacing={3}>
              <Typography variant="h2">Activity categories</Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Built around the stuff guys actually do. Pick your lane and pull up.
              </Typography>
              <Stack
                gap={2}
                display="flex"
                direction="row"
                sx={{
                  flexWrap: { xs: "wrap", md: "wrap" },
                  pb: 1
                }}>
                {categories.map(category => (
                  <Box
                    key={category}
                    sx={{
                      maxWidth: { xs: 160, md: 160 },
                      border: `2px solid ${tokens.colors.border}`,
                      borderRadius: tokens.radius.base,
                      padding: "1rem 1.5rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.18em",
                      fontWeight: 600,
                      backgroundColor: tokens.colors.background.default
                    }}>
                    {category}
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Container>
        </Box>

        {/* <Box component="section" sx={{ py: { xs: 6, md: 10 } }}>
          <Container>
            <Stack gap={4}>
              <Box>
                <Typography variant="h2">Built for real crews</Typography>
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  Built for men who want real friends, not screen time.
                </Typography>
              </Box>
              <Grid container gap={3}>
                {testimonials.map(item => (
                  <Grid item xs={12} md={4} key={item.name}>
                    <Box
                      sx={{
                        border: `2px solid ${tokens.colors.border}`,
                        borderRadius: tokens.radius.card,
                        padding: "1.5rem",
                        backgroundColor: tokens.colors.background.surface,
                        height: "100%"
                      }}>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        “{item.quote}”
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                        {item.name}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </Box> */}

        <Box component="section" id="faq" sx={{ py: { xs: 6, md: 9 } }}>
          <Container>
            <Stack gap={3}>
              <Typography variant="h2">FAQ</Typography>
              <Grid container spacing={3}>
                {faq.map(item => (
                  <Grid item xs={12} md={4} key={item.question}>
                    <Box
                      sx={{
                        border: `2px solid ${tokens.colors.border}`,
                        borderRadius: tokens.radius.card,
                        padding: "1.5rem",
                        backgroundColor: tokens.colors.background.surface,
                        height: "100%"
                      }}>
                      <Typography variant="h6" sx={{ textTransform: "uppercase" }}>
                        {item.question}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                        {item.answer}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </Box>

        <Box
          component="section"
          id="download"
          sx={{ py: { xs: 6, md: 10 }, backgroundColor: tokens.colors.background.surface }}>
          <Container>
            <Stack
              spacing={4}
              sx={{
                border: `2px solid ${tokens.colors.accent.primary}`,
                borderRadius: tokens.radius.card,
                padding: { xs: 3, md: 5 },
                backgroundColor: tokens.colors.background.default
              }}>
              <Box>
                <Typography variant="h2">Join the crew</Typography>
                <Typography variant="body1" sx={{ color: "text.secondary", mt: 1 }}>
                  Download BroStrike and lock in your next real-life hangout.
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Button variant="contained" color="primary" href="#">
                  App Store
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  href="https://play.google.com/store/apps/details?id=com.brostrike">
                  Google Play
                </Button>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </Box>

      <Divider sx={{ borderColor: tokens.colors.border }} />

      <Box component="footer" sx={{ py: 4, backgroundColor: tokens.colors.background.default }}>
        <Container>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
            <Typography variant="subtitle2" sx={{ textTransform: "uppercase" }}>
              BroStrike
            </Typography>
            <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap" }}>
              <Link href="/privacy-policy">Privacy</Link>
              <Link href="/terms-of-service">Terms</Link>
              <Link href="mailto:contact@brostrike.com">Contact</Link>
              <Link href="https://instagram.com" target="_blank" rel="noreferrer">
                Instagram
              </Link>
              <Link href="https://twitter.com" target="_blank" rel="noreferrer">
                Twitter
              </Link>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {showCookieBanner && (
        <Box
          role="dialog"
          aria-live="polite"
          sx={{
            position: "fixed",
            insetInline: 0,
            bottom: 0,
            zIndex: 1300,
            borderTop: `2px solid ${tokens.colors.border}`,
            backgroundColor: tokens.colors.background.surface,
            boxShadow: tokens.shadows.soft
          }}>
          <Container sx={{ py: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between">
              <Stack spacing={0.5}>
                <Typography variant="subtitle1" sx={{ textTransform: "uppercase" }}>
                  Essential cookies required
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  We use mandatory cookies to keep BroStrike running. By continuing, you agree to their use.{" "}
                  <Link href="/privacy-policy">Learn more</Link>
                </Typography>
              </Stack>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowCookieBanner(false)}
                sx={{ flexShrink: 0 }}>
                Got it
              </Button>
            </Stack>
          </Container>
        </Box>
      )}
    </Box>
  )
}

export default Home
